import { DRAG_HANDLE_CLASS, DRAG_HANDLE_ID_PREFIX, HANDLE, WIDGET_LOCK_CLASS } from 'constants/bounding-box';
import { MutableRefObject } from 'react';
import { OnSelect, OnSelectEnd } from 'react-selecto';
import { colors } from 'theme/colors';
import { WidgetId } from 'types/idTypes';
import { Widget, WidgetType } from 'types/widget.types';
import { getRotatDegFromStyle } from 'utils/dom';
import { parseStrictNumber } from 'utils/number';
import { checkBothPortsSelected } from 'widgets/Lines/LineWidget/LineWidget.helpers';
import { Rect } from 'widgets/Lines/LineWidget/LineWidget.types';
import { WIDGET_BOUNDING_BOX_PADDING_MAP } from 'widgets/Widget.config';
import { getBoundingRectOfRotatedWidget, getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

import { ActiveWidgetIds, ActiveWidgets } from '../../store/widgetControlSlice';
import {
  DEFAULT_WIDGET_BOUNDING_BOX_CONFIG,
  SMALL_BOUNDING_BOX_HEIGHT,
  WIDGET_BOUNDING_BOX_CONFIG,
} from './BoundingBox.config';
import {
  BoundingBoxFunction,
  Compute,
  Frame,
  Hide,
  Match,
  OnDragGroupEndEvent,
  OnDragGroupEvent,
  OnResizeGroupEndEvent,
  OnResizeGroupEvent,
  ResizingState,
  SelectoTarget,
  SetHandleStyle,
  UnsetHandleStyle,
  WidgetEvent,
  WidgetHandleDirection,
} from './BoundingBox.types';
import { WidgetBoundingBoxRef } from './useBoundingBox/useBoundingBox.types';

/**
 * Calculates the position and dimensions of a group of draggable targets.
 *
 * @param e - The drag event object, which could be either OnDragGroupEndEvent or OnDragGroupEvent.
 * @param frameMap - A map containing frame information for each draggable target.
 * @param pageScrollOnDrag - An object containing the current page scroll positions in the form of [x, y].
 * @param zoom - The current zoom level of the page.
 *
 * @returns A Rect object containing the width, height, and x, y coordinates of the group.
 */
export const getGroupPosition = (
  e: OnDragGroupEndEvent | OnDragGroupEvent | OnResizeGroupEndEvent | OnResizeGroupEvent,
  frameMap: Frame,
  pageScrollOnDrag: { current: [number, number] },
  zoom: number,
  allowedIds?: string[],
): Rect => {
  // Get smallest left and top among all targets
  const { left, top, width, height } = getGroupTargetDimensions(e, frameMap, pageScrollOnDrag, zoom, allowedIds);

  return { width, height, x: left, y: top };
};

/**
 * Extracts and calculates the rotation angle (in degrees) of an HTML element based on its computed style.
 *
 * @param element The HTML element from which to extract the rotation angle.
 * @returns The rotation angle of the element in degrees. Returns 0 degrees if no rotation is found in the computed style.
 */
export const getRotationFromComputedStyle = (element: HTMLElement): number => {
  const style = window.getComputedStyle(element);
  const transform = style.transform;

  if (transform && transform !== 'none') {
    const matrix = new DOMMatrixReadOnly(transform);
    const angle = Math.atan2(matrix.m21, matrix.m11) * (180 / Math.PI); // Rotation in degrees
    return angle;
  }
  return 0; // Default to 0 degrees if no rotation is found
};

/**
 * Calculates the smallest left and top positions among a group of draggable targets.
 *
 * @param e - The drag event object, which could be either OnDragGroupEndEvent or OnDragGroupEvent.
 * @param frameMap - A map containing frame information for each draggable target.
 * @param pageScrollOnDrag - An object containing the current page scroll positions in the form of [x, y].
 * @param zoom - The current zoom level of the page.
 *
 * @returns An object containing the smallest left and top positions among the targets.
 *
 * @throws Will throw an error if it cannot calculate the smallest left and top positions.
 */
export const getGroupTargetDimensions = (
  e: OnDragGroupEndEvent | OnDragGroupEvent | OnResizeGroupEndEvent | OnResizeGroupEvent,
  frameMap: Frame,
  pageScrollOnDrag: { current: [number, number] },
  zoom: number,
  allowedIds?: string[],
): { left: number; top: number; width: number; height: number } => {
  let smallestLeft = Infinity;
  let smallestTop = Infinity;
  let largestRight = -Infinity;
  let largestBottom = -Infinity;

  e.targets.forEach((target) => {
    if (allowedIds && !allowedIds.includes(target.id)) return;
    const frame = frameMap.get(target);
    if (!frame || groupIdCache.isResponsiveGroupMember(target?.id)) return;

    const pageScrollAdjustment = pageScrollOnDrag.current;
    const newLeftPx = parseStrictNumber(target.style.left) + (frame.translate[0] + pageScrollAdjustment[0] / zoom);
    const newTopPx = parseStrictNumber(target.style.top) + (frame.translate[1] + pageScrollAdjustment[1] / zoom);

    // Get the width and height of the child element
    const childDiv = target;
    const childStyle = window.getComputedStyle(childDiv);
    const width = parseStrictNumber(childStyle.width || '0');
    const height = parseStrictNumber(childStyle.height || '0');

    const boundingRect = getBoundingRectOfRotatedWidget(
      newLeftPx,
      newTopPx,
      width,
      height,
      getRotationFromComputedStyle(childDiv as HTMLElement),
    );

    smallestLeft = Math.min(smallestLeft, boundingRect.leftPx);
    smallestTop = Math.min(smallestTop, boundingRect.topPx);
    largestRight = Math.max(largestRight, boundingRect.leftPx + boundingRect.widthPx);
    largestBottom = Math.max(largestBottom, boundingRect.topPx + boundingRect.heightPx);
  });

  const width = largestRight - smallestLeft;
  const height = largestBottom - smallestTop;

  return { left: smallestLeft, top: smallestTop, width, height };
};

export const getWidgetElements = (
  activeWidgetIds: ActiveWidgetIds,
  widgetRefs: WidgetBoundingBoxRef,
): HTMLElement[] => {
  const widgetElements: HTMLElement[] = [];

  // Map to check for line widgets that should be included in the moveable targets
  const attachedLineWidgets: { [key: WidgetId]: boolean } = {};

  activeWidgetIds.forEach((widgetId) => {
    const isGroupWidget = getWidgetTypeFromId(widgetId) === WidgetType.Group;
    const widgetEl = widgetRefs[widgetId]?.element;

    // the responsive widget can conditionally render widget members
    // so we need to check if the widget is in the dom
    const widgetExistsInDom = document.getElementById(widgetId)!!;

    // the group widget is active but not a moveable target (is not resizble etc and does not need the bounding box events)
    if (widgetEl && !isGroupWidget && widgetExistsInDom) widgetElements.push(widgetEl);

    // If the widget is connected to a line widget, add the line widget to the moveable targets if both ports are selected
    const connectedLines = lineConnectionCache.getAttachedLines(widgetId);
    connectedLines?.forEach((lineId) => {
      // Skip if the line is already selected
      if (activeWidgetIds.includes(lineId)) return;

      // Both ports are selected
      if (attachedLineWidgets[lineId]) {
        const lineWidgetEl = widgetRefs[lineId]?.element;
        const lineWidgetExistsInDom = document.getElementById(lineId)!!;
        if (lineWidgetEl && lineWidgetExistsInDom) widgetElements.push(lineWidgetEl);
      } else {
        // Only one port has been detected so far
        attachedLineWidgets[lineId] = true;
      }
    });
  });

  return widgetElements;
};

export const getWidgetHandles = (
  activeWidgets: ActiveWidgets,
  selectedWidget: Widget,
  isDrag = false,
  zoom = 1,
): string[] => {
  if (!activeWidgets.length) return [];
  // when dragging show no handles
  if (isDrag) return [];

  const isMultipleSelect = activeWidgets.length > 1;
  if (isMultipleSelect) return HANDLE.CORNERS;

  // For group widgets - check if there should be side + corner handles or just corner handles
  // should have side handles if there is 1 shape + x text widgets
  const isGroupWidget = !!activeWidgets[0].groupId && activeWidgets[0].groupMembers.length!!;
  if (isGroupWidget) {
    let hasSideHandles = true;
    let shapeCount = 0;

    const groupWidget = activeWidgets[0];
    for (let i = 0; i < groupWidget.groupMembers.length; i += 1) {
      const memberId = groupWidget.groupMembers[i];
      const memberType = getWidgetTypeFromId(memberId);

      const isResponsiveShapeType = memberType === WidgetType.Text || memberType === WidgetType.BasicShape;
      if (!isResponsiveShapeType) {
        hasSideHandles = false;
        break;
      }

      if (memberType === WidgetType.BasicShape) shapeCount += 1;

      if (isResponsiveShapeType && getRotatDegFromStyle(document.getElementById(memberId)?.style) !== 0) {
        hasSideHandles = false;
        break;
      }
    }

    // only for 1 shape + x text widgets
    if (shapeCount !== 1) hasSideHandles = false;

    if (hasSideHandles) return HANDLE.SIDE_CORNERS;

    return HANDLE.CORNERS;
  }

  const activeWidget = activeWidgets[0].responsiveGroupId || activeWidgets[0].id;
  const widgetType = getWidgetTypeFromId(activeWidget);
  const widgetConfig = WIDGET_BOUNDING_BOX_CONFIG[widgetType];

  let handle = HANDLE.CORNERS;

  // check if the widget has a custom handle
  const hasNoCustomHandle = !widgetConfig || !widgetConfig.customHandle;
  if (hasNoCustomHandle) handle = HANDLE.ALL;
  else {
    if (typeof widgetConfig.customHandle === 'function') handle = widgetConfig.customHandle(selectedWidget);
    else if (widgetConfig.customHandle) handle = widgetConfig.customHandle;
  }

  // adjust the handles for small bounding boxes
  const widgetEl = document.getElementById(activeWidget);
  if (!widgetEl) return handle;

  // check if the bounding box is small
  const isSmallWidget = widgetEl.clientHeight * zoom < SMALL_BOUNDING_BOX_HEIGHT;
  const isTextWidget = widgetType === WidgetType.Text;

  const isStatchartWithSideHandle = widgetType === WidgetType.StatChart && handle.length === 6;
  if (isSmallWidget && (isTextWidget || isStatchartWithSideHandle)) {
    handle = HANDLE.TOP_LEFT_RIGHT;
  }

  return handle;
};

export const getWidgetRotatable = (activeWidgetIds: ActiveWidgetIds): boolean => {
  if (!activeWidgetIds?.length) return false;

  // Rotation is disabled for group widgets
  const hasParentWidget = activeWidgetIds.find((wId) => !!groupIdCache.getParentId(wId));
  if (hasParentWidget) return false;

  // If any of the active widgets are not rotatable, return false
  for (const widgetId of activeWidgetIds) {
    const widgetType = getWidgetTypeFromId(widgetId);
    const widgetConfig = WIDGET_BOUNDING_BOX_CONFIG[widgetType];
    if (widgetConfig && widgetConfig.hasOwnProperty('rotatable') && !widgetConfig.rotatable) {
      return false;
    }
  }

  const isMultipleSelect = activeWidgetIds.length > 1;
  if (isMultipleSelect) return true;

  return true;
};

export const getWidgetResizable = (activeWidgetIds: ActiveWidgetIds): boolean => {
  if (!activeWidgetIds?.length) return false;

  const isGroup = activeWidgetIds.length > 1;
  if (isGroup) return true;

  const widgetType = getWidgetTypeFromId(activeWidgetIds[0]);
  const widgetConfig = WIDGET_BOUNDING_BOX_CONFIG[widgetType];

  if (!widgetConfig || !widgetConfig.hasOwnProperty('resizable')) return true;

  return widgetConfig.resizable ?? true;
};

/**
 * Get widget configruation for `edgeDraggable` property
 * Will be passed to Moveable `edgeDraggable` prop if defined. Default edgeDraggable is TRUE.
 *
 * @param activeWidgetIds active widgets
 * @returns boolean
 */
export const getWidgetEdgeDraggable = (activeWidgetIds: ActiveWidgetIds): boolean => {
  if (!activeWidgetIds?.length) return true;

  const isGroup = activeWidgetIds.length > 1;
  if (isGroup || !activeWidgetIds.length) return true;

  const widgetType = getWidgetTypeFromId(activeWidgetIds[0]);
  const widgetConfig = WIDGET_BOUNDING_BOX_CONFIG[widgetType];

  if (!widgetConfig || !widgetConfig.hasOwnProperty('edgeDraggable')) return true;

  return widgetConfig.edgeDraggable ?? true;
};

export const getHideDefaultLines = (activeWidgetIds: ActiveWidgetIds): boolean => {
  if (!activeWidgetIds?.length) return true;

  const isGroup = activeWidgetIds.length > 1;
  if (isGroup || !activeWidgetIds.length) return false;

  const widgetType = getWidgetTypeFromId(activeWidgetIds[0]);
  const widgetConfig = WIDGET_BOUNDING_BOX_CONFIG[widgetType];

  if (!widgetConfig || !widgetConfig.hasOwnProperty('hideDefaultLines')) return false;

  return widgetConfig.hideDefaultLines ?? false;
};

export const getKeepAspectRatioHandles = (activeWidgetIds: ActiveWidgetIds): string[] | null => {
  if (!activeWidgetIds.length) return null;

  const isGroup = activeWidgetIds.length > 1;
  if (isGroup) return null;

  const widgetType = getWidgetTypeFromId(activeWidgetIds[0]);
  const widgetConfig = WIDGET_BOUNDING_BOX_CONFIG[widgetType];

  const defaultHandles = DEFAULT_WIDGET_BOUNDING_BOX_CONFIG.keepAspectRatioHandles as string[];

  if (!widgetConfig || !widgetConfig.hasOwnProperty('keepAspectRatioHandles')) return defaultHandles;

  return widgetConfig.keepAspectRatioHandles || defaultHandles;
};

/**
 * Check if an element is included in the active widget ids.
 *
 * @param activeWidgetIds
 * @param element
 * @returns
 */
export const checkActiveWidgetIdByTarget = (activeWidgetIds: ActiveWidgetIds, element: Element): boolean => {
  return activeWidgetIds.includes(element?.id);
};

/**
 * Check if an element should be included in the save call in the react moveable event end functions.
 * (i.e. onDragEnd, onRotateEnd, onResizeEnd);
 *
 * TRUE if the element is included in the active widget ids or if the element is a line widget and both ports are selected.
 *
 * @param activeWidgetIds
 * @param element
 * @returns
 */
export const checkShouldSaveTarget = (activeWidgetIds: ActiveWidgetIds, element: Element): boolean => {
  return checkActiveWidgetIdByTarget(activeWidgetIds, element) || checkBothPortsSelected(element?.id);
};

export const getDragHandleWidgetId = (dragId: string) => {
  const widgetId = dragId.replace(DRAG_HANDLE_ID_PREFIX, '');
  return widgetId;
};

export const getWidgetTargetIds = (targets: (SelectoTarget | HTMLElement)[]): ActiveWidgetIds => {
  const widgetIds: ActiveWidgetIds = [];
  const widgetIdMap: { [key: WidgetId]: boolean } = {};

  targets.forEach((target: SelectoTarget | HTMLElement) => {
    let widgetId = '';

    // the drag handle is counted as a selecto target
    // get the widget id the drag handle belongs to
    if (target.classList.contains(DRAG_HANDLE_CLASS)) widgetId = getDragHandleWidgetId(target.id);
    else widgetId = target.id;

    // while drag selecting both the widget and the draghandle are targets
    // add only one id in this case
    if (!widgetIdMap[widgetId]) {
      widgetIds.push(widgetId);
      widgetIdMap[widgetId] = true;
    }
  });

  return widgetIds;
};

interface ExtendWidgetCreator {
  activeWidgetIds: ActiveWidgetIds;
  widgetRefs: WidgetBoundingBoxRef;
  resizeStateRef: MutableRefObject<ResizingState>;
  frameMap: Frame;
  compute: Compute;
  match: Match;
  hide: Hide;
  saveWidget: (id: WidgetId, data: object) => void;
  resetActionState: () => void;
  setHandleStyle: SetHandleStyle;
  unsetHandleStyle: UnsetHandleStyle;
}
/**
 * Allow widgets to access and override bounding box functions.
 * If there is a custom boundingBox event function in the widget call that function else call the default boundingBox function
 * e.g. a widget can implement their own onDrag or add logic before or after the onDrag event
 *
 * @param activeWidgetIds all active widget ids
 * @param widgetRefs all widget refs
 * @param resizeStateRef the resize state ref used for setting the handle style
 * @param frameMap frameMap with translate and rotate exposed to widget
 * @param Compute smartGuidelines function exposed to widget
 * @param Match smartGuidelines function exposed to widget
 * @param Hide smartGuidelines function exposed to widget
 * @param saveWidget Used for saving  updated widget data after the bounding box event has finished - exposed to `*.End` functions
 * @param resetActionState Used for resetting the action state after the bounding box event has finished - exposed to `*.End` functions
 * @param setHandleStyle Used for setting the handle style
 * @param unsetHandleStyle Used for unsetting the handle style
 * @param boundingBoxFunction a function from boundingbox that should be passed to the widget e.g. onDrag
 * @param boundingBoxEvent event that is passed to the boundingBoxFunction e.g. OnDragEvent
 */

export const extendWidgetCreator =
  ({
    activeWidgetIds,
    widgetRefs,
    frameMap,
    compute,
    match,
    hide,
    saveWidget,
    resetActionState,
    setHandleStyle,
    unsetHandleStyle,
    resizeStateRef,
  }: ExtendWidgetCreator) =>
  (boundingBoxFunction: any, functionName: WidgetEvent) =>
  (boundingBoxEvent: any) => {
    const isSingleWidgetSelected = activeWidgetIds.length === 1;
    if (!isSingleWidgetSelected) return boundingBoxFunction(boundingBoxEvent);

    const [widgetId] = activeWidgetIds;
    const widgetOverrideSettings = widgetRefs[widgetId];
    if (!widgetOverrideSettings) return boundingBoxFunction(boundingBoxEvent);

    const widgetCustomFunction = widgetOverrideSettings[functionName as keyof BoundingBoxFunction];
    if (!widgetCustomFunction) return boundingBoxFunction(boundingBoxEvent);

    const isEndEvent =
      functionName === WidgetEvent.onDragEnd ||
      functionName === WidgetEvent.onResizeEnd ||
      functionName === WidgetEvent.onRotateEnd;

    // expose more internal bounding box state by passing them as arguments here:
    return widgetCustomFunction({
      event: boundingBoxEvent,
      [functionName]: boundingBoxFunction,
      smartGuide: { compute, match, hide },
      frameMap,
      resizeStateRef,
      setHandleStyle,
      unsetHandleStyle,
      ...(isEndEvent ? { saveWidget, resetActionState } : {}),
    });
  };

/**
 * Generates direction string from direction provided by Moveable resize
 * events.
 *
 * @param moveableDirection number[] direction provided by Moveable events
 * @returns
 */
export const getDirectionAsString = (moveableDirection: number[]): WidgetHandleDirection => {
  let direction = '';
  switch (moveableDirection[1]) {
    case -1:
      direction = 'n';
      break;
    case 1:
      direction = 's';
      break;
    default:
      break;
  }

  switch (moveableDirection[0]) {
    case -1:
      direction += 'w';
      break;
    case 1:
      direction += 'e';
      break;
    default:
      break;
  }

  return direction as WidgetHandleDirection;
};

/**
 * Check if the input path has a locked widget and return the locked widget element if it exists
 *
 * @param e OnSelect or OnSelectEnd event
 */
export const getLockedWidgetEl = (e: OnSelect | OnSelectEnd) => {
  const inputPath = e.inputEvent.path || (e.inputEvent.composedPath && e.inputEvent.composedPath()) || [];
  let lockedWidgetEl: Element | null = null;

  for (let i = 0; i < inputPath.length; i++) {
    const el = inputPath[i];
    if (el?.classList?.contains(WIDGET_LOCK_CLASS)) {
      lockedWidgetEl = el;
      break;
    }
  }

  return lockedWidgetEl;
};

/**
 * Set the background color of the resize handle to indicate that the resize is actually started.
 * Obs: mutates the resizeStateRef
 * @param resizeStateRef the resize state ref used for setting the handle style
 * @param moveableDirection number[] direction provided by Moveable events
 * @param selectorPrefix selector prefix for the bounding box in case of multiple moveable instances
 * @returns
 *
 */
export const setHandleStyle: SetHandleStyle = (resizeStateRef, moveableDirection, selectorPrefix) => {
  // If actual dragging is not started yet, or, this one is first drag (e.isFirstDrag is not behaving as expected)
  if (!resizeStateRef.current.actualResizingStarted) {
    const direction = getDirectionAsString(moveableDirection);
    const allHandleSelector = `${selectorPrefix ? selectorPrefix + ' ' : ''}.moveable-resizable`;
    const allHandleEls = document.querySelectorAll(allHandleSelector);

    if (allHandleEls && allHandleEls.length) {
      allHandleEls.forEach((handleEl: Element) => {
        const el = handleEl as HTMLElement;

        if (el.classList.contains(`moveable-${direction}`)) {
          el.style.background = colors.upgrade[500];
          el.style.opacity = '1';
        } else {
          el.style.display = 'none';
          el.style.opacity = '0';
        }
      });
    }

    // record state, prevent multiple call to this code block
    resizeStateRef.current.actualResizingStarted = true;
    resizeStateRef.current.direction = direction;
  }
};

/**
 * Resets the background color of the bounding box handle and resets the resizeStateRef
 * Obs: mutates the resizeStateRef
 * @param resizeStateRef the resize state ref used for setting the handle style
 * @param moveableDirection number[] direction provided by Moveable events
 * @param selectorPrefix selector prefix for the bounding box in case of multiple moveable instances
 * @returns
 *
 */
export const unsetHandleStyle: UnsetHandleStyle = (resizeStateRef, selectorPrefix) => {
  const allHandleSelector = `${selectorPrefix ? selectorPrefix + ' ' : ''}.moveable-resizable`;
  const allHandleEls = document.querySelectorAll(allHandleSelector);

  if (allHandleEls) {
    allHandleEls.forEach((handleEl: Element) => {
      const el = handleEl as HTMLElement;
      el.style.background = '';
      el.style.display = '';
      el.style.opacity = '';
    });
  }

  // Restore resizing state ref
  resizeStateRef.current.actualResizingStarted = false;
  resizeStateRef.current.direction = '';
};

/**
 * Get the padding for the bounding box of the selected widget type
 *
 * @param widgetType The type of the widget
 * @returns number - the padding for the bounding box of the widget
 */
export const getWidgetPadding = (widgetType: WidgetType): number => {
  if (!widgetType) return 0;
  // We don't want the padding to interfer with the smart guides while dragging/resizing/rotating
  if (!WIDGET_BOUNDING_BOX_PADDING_MAP[widgetType]) return 0;

  return WIDGET_BOUNDING_BOX_PADDING_MAP[widgetType] as number;
};

/**
 * Retrieves the start frame for a target element from the frame map.
 * If the target element is not present in the frame map, it adds it with default values.
 * If the target element has a rotation style, it updates the rotation value in the frame map.
 *
 * @param targetEl
 * @param frameMap - The map containing the frame data for each bounding box target element.
 * @returns the Frame for the target element.
 */
export const getStartFrame = (targetEl: HTMLElement | SVGElement, frameMap: Frame) => {
  if (!frameMap.has(targetEl)) {
    frameMap.set(targetEl, {
      translate: [0, 0],
      rotate: 0,
    });
  }

  const rotateDeg = getRotatDegFromStyle(targetEl.style);
  if (!Number.isNaN(rotateDeg)) {
    frameMap.set(targetEl, {
      translate: [0, 0],
      rotate: rotateDeg || 0,
    });
  }

  return frameMap.get(targetEl);
};

/**
 * Determine if there is enough space for a full scrollStep, if not adjust the actual scrollX
 *
 * @param scrollStep - The step size of the scroll.
 * @param direction - The direction of the scroll (-1 for top, 1 for bottom).
 * @param scrollHeight - The total height of the scrollable content.
 * @param pageScrollEl - The element representing the page scroll.
 * @returns The actual scroll distance.
 */
export const calculateActualScrollX = (
  scrollStep: number,
  direction: number,
  scrollWidth: number,
  pageScrollEl: HTMLElement | null | undefined,
): number => {
  if (!pageScrollEl) return 0;

  // when page scrolls to the right, pageScrollEl.scrollWidth could be increased while dragging, use the original scrollWidth for consistent calculation
  // direction -1 means drag to left and 1 means to the right
  const { scrollLeft, clientWidth } = pageScrollEl;
  if (direction === -1 && scrollLeft - scrollStep >= 0) {
    return scrollStep;
  } else if (direction === -1 && scrollLeft - scrollStep < 0) {
    return scrollLeft;
  } else if (direction === 1 && scrollWidth - (scrollLeft + clientWidth) - scrollStep >= 0) {
    return scrollStep;
  } else {
    return scrollWidth - (scrollLeft + clientWidth);
  }
};

/**
 * Determine if there is enough space for a full scrollStep, if not adjust the actual scrollY
 *
 * @param scrollStep - The step size of the scroll.
 * @param direction - The direction of the scroll (-1 for top, 1 for bottom).
 * @param scrollHeight - The total height of the scrollable content.
 * @param pageScrollEl - The element representing the page scroll.
 * @returns The actual scroll distance.
 */
export const calculateActualScrollY = (
  scrollStep: number,
  direction: number,
  scrollHeight: number,
  pageScrollEl: HTMLElement | null | undefined,
): number => {
  if (!pageScrollEl) return 0;

  // when page scrolls to the bottom, pageScrollEl.scrollHeight could be increased while dragging, use the original scrollHeight for consistent calculation
  // direction -1 means drag to top and 1 means to the bottom
  const { scrollTop, clientHeight } = pageScrollEl;
  if (direction === -1 && scrollTop - scrollStep >= 0) {
    return scrollStep;
  } else if (direction === -1 && scrollTop - scrollStep < 0) {
    return scrollTop;
  } else if (direction === 1 && scrollHeight - (scrollTop + clientHeight) - scrollStep >= 0) {
    return scrollStep;
  } else {
    return scrollHeight - (scrollTop + clientHeight);
  }
};

/**
 * Returns an array of indices representing the non-active target elements in the widgetElements array.
 *
 * @param widgetElements - An array of widget HTMLElements
 * @param activeWidgetIds - An array of active widget IDs.
 *
 * @returns An array of indices representing the non-active target elements.
 */
export const getNonActiveTargetIdxs = (widgetElements: HTMLElement[], activeWidgetIds: WidgetId[]) => {
  if (!widgetElements || !widgetElements.length) return [];

  const nonActiveTargetIdxs: number[] = [];
  widgetElements.forEach((widgetEl, i) => {
    const widgetId = widgetEl?.id;
    if (!widgetId) return;
    if (!activeWidgetIds.includes(widgetId)) nonActiveTargetIdxs.push(i);
  });

  return nonActiveTargetIdxs;
};

/**
 * Return the handles that should be hidden (but still functional)
 * E.g. for a bounding box with a small height the side handles should be hidden but still functional
 *
 * @param widgetHandles - The current handles of the widget.
 * @param isSingleWidgetSelected - A boolean indicating whether a single widget is selected.
 * @param selectedWidgetId - The ID of the selected widget.
 * @param zoom - The zoom level.
 *
 * @returns An array with the hidden handles ['w', 'e'].
 */
export const getHiddenWidgetHandles = (
  widgetHandles: string[],
  isSingleWidgetSelected: boolean,
  selectedWidgetId: WidgetId | undefined,
  zoom: number,
): string[] => {
  if (!isSingleWidgetSelected || !selectedWidgetId) return [];
  // the text does not have hidden side handles for small bounding boxes
  if (getWidgetTypeFromId(selectedWidgetId) === WidgetType.Text) return [];
  // statcharts do not have hidden side handles for small bounding boxes
  if (getWidgetTypeFromId(selectedWidgetId) === WidgetType.StatChart && widgetHandles.length === 6) return [];

  // only for widgets with all 8 handles (shapes) or 6 handles
  if (widgetHandles.length !== 8 && widgetHandles.length !== 6) return [];

  const widgetEl = document.getElementById(selectedWidgetId);
  if (!widgetEl) return [];

  const isSmallHeight = widgetEl.clientHeight * zoom < SMALL_BOUNDING_BOX_HEIGHT;
  const isSmallWidth = widgetEl.clientWidth * zoom < SMALL_BOUNDING_BOX_HEIGHT;

  // if the widget is small in both height and width, hide all side handles
  if (isSmallHeight && isSmallWidth) return HANDLE.TOP_BOTTOM_LEFT_RIGHT;
  // if the widget is small in height but not width, hide the side handles
  if (isSmallHeight) return HANDLE.LEFT_RIGHT;
  // if the widget is small in width but not height, hide the top/bottom handles
  if (isSmallWidth) return HANDLE.TOP_BOTTOM;

  return [];
};
